import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Box, TextField, Button, Typography, Stack, Tabs, Tab } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useForm, Controller } from 'react-hook-form';
import {
  useUsageLimitControllerAdminUsageLimitsQuery,
  useConcurrentLimitSetControllerSetConcurrentLimitMutation,
  useUsageLimitSetControllerSetLimitsMutation,
  useCompanyControllerGetCompanyAsAdminQuery,
  useCompanyVoicesSetControllerSetCompanyVoicesMutation,
  useCompanyLlmProvidersSetControllerSetCompanyLlmProvidersMutation,
  useCompanyTranscribersSetControllerSetCompanyTranscribersMutation,
  useVoiceListControllerVoiceListQuery,
  useLlmProviderListControllerAdminProviderListQuery,
} from '@shared/services/apiService/apiService';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { useErrorHandler } from '@shared/hooks/useErrorHandler';
import {
  buildUsageLimitItemsFromForm,
  formatOptionLimitsConflictMessage,
  getEmptyCompanyLimitsFormValues,
  mapRemainingLimitsToFormValues,
  type CompanyLimitsFormValues,
} from './utils';
import { UsageLimitsFields } from './components/UsageLimitsFields';
import { OptionLimitsFields } from './components/OptionLimitsFields';

interface CompanyLimitsPopupProps {
  companyId: string;
  showDialogFlag: boolean;
  onSuccess: () => void;
  closeDialog: () => void;
}

export const CompanyLimitsPopup: React.FC<CompanyLimitsPopupProps> = ({
  companyId,
  showDialogFlag,
  onSuccess,
  closeDialog,
}) => {
  const { t } = useTranslation();
  const { showError, extractErrorMessage } = useErrorHandler();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedVoiceIds, setSelectedVoiceIds] = useState<string[]>([]);
  const [selectedLlmProviderIds, setSelectedLlmProviderIds] = useState<string[]>([]);
  const [selectedTranscriberIds, setSelectedTranscriberIds] = useState<string[]>([]);

  const { control, handleSubmit, reset, formState } = useForm<CompanyLimitsFormValues>({
    defaultValues: getEmptyCompanyLimitsFormValues(),
    mode: 'onSubmit',
  });
  const { errors } = formState;

  const { data: usageLimitsData } = useUsageLimitControllerAdminUsageLimitsQuery(
    { id: companyId },
    { skip: !showDialogFlag, refetchOnMountOrArgChange: true },
  );
  const { data: company, refetch: refetchCompany } = useCompanyControllerGetCompanyAsAdminQuery(
    { id: companyId },
    { skip: !showDialogFlag || !companyId, refetchOnMountOrArgChange: true },
  );
  const { data: catalogVoices } = useVoiceListControllerVoiceListQuery(undefined, {
    skip: !showDialogFlag,
  });
  const { data: catalogProviders } = useLlmProviderListControllerAdminProviderListQuery(undefined, {
    skip: !showDialogFlag,
  });
  const [setConcurrentLimitMutation, { isLoading: isSavingConcurrent }] =
    useConcurrentLimitSetControllerSetConcurrentLimitMutation();
  const [setUsageLimitsMutation, { isLoading: isSavingUsage }] =
    useUsageLimitSetControllerSetLimitsMutation();
  const [setVoices, { isLoading: isSavingVoices }] =
    useCompanyVoicesSetControllerSetCompanyVoicesMutation();
  const [setLlmProviders, { isLoading: isSavingLlmProviders }] =
    useCompanyLlmProvidersSetControllerSetCompanyLlmProvidersMutation();
  const [setTranscribers, { isLoading: isSavingTranscribers }] =
    useCompanyTranscribersSetControllerSetCompanyTranscribersMutation();

  const isSaving =
    isSavingConcurrent ||
    isSavingUsage ||
    isSavingVoices ||
    isSavingLlmProviders ||
    isSavingTranscribers;

  const resetToLatestValues = useCallback(() => {
    if (usageLimitsData) {
      reset(mapRemainingLimitsToFormValues(usageLimitsData));
      return;
    }

    reset(getEmptyCompanyLimitsFormValues());
  }, [reset, usageLimitsData]);

  useEffect(() => {
    reset(getEmptyCompanyLimitsFormValues());
    setSelectedVoiceIds([]);
    setSelectedLlmProviderIds([]);
    setSelectedTranscriberIds([]);
    setActiveTab(0);
  }, [companyId, reset]);

  useEffect(() => {
    if (!showDialogFlag) {
      return;
    }
    setActiveTab(0);
    resetToLatestValues();
  }, [showDialogFlag, resetToLatestValues]);

  useEffect(() => {
    if (!showDialogFlag || company === undefined) {
      return;
    }
    setSelectedVoiceIds(company.voices?.map((v) => v.id) ?? []);
    setSelectedLlmProviderIds(company.llmProviders?.map((p) => p.id) ?? []);
    setSelectedTranscriberIds(company.transcribers?.map((t) => t.id) ?? []);
  }, [showDialogFlag, company]);

  const handleClose = () => {
    resetToLatestValues();
    if (company !== undefined) {
      setSelectedVoiceIds(company.voices?.map((v) => v.id) ?? []);
      setSelectedLlmProviderIds(company.llmProviders?.map((p) => p.id) ?? []);
      setSelectedTranscriberIds(company.transcribers?.map((t) => t.id) ?? []);
    }
    setActiveTab(0);
    closeDialog();
  };

  const onSubmit = async (values: CompanyLimitsFormValues) => {
    const usageItems = buildUsageLimitItemsFromForm({
      callHoursMonth: values.callHoursMonth,
      sttHoursMonth: values.sttHoursMonth,
      llmTokensMonth: values.llmTokensMonth,
      ttsCharactersMonth: values.ttsCharactersMonth,
    });
    if (usageItems === null) {
      showError({
        title: t('superAdminMainPage.companies.companyLimits.validationTitle'),
        message: t('superAdminMainPage.companies.companyLimits.invalidUsageLimitValue'),
      });
      return;
    }

    try {
      await setConcurrentLimitMutation({
        id: companyId,
        concurrentLimitSetRequestDto: {
          companyConcurrentLimit: Number(values.concurrentLimit),
        },
      }).unwrap();

      if (usageItems.length > 0) {
        await setUsageLimitsMutation({
          id: companyId,
          usageLimitSetRequestDto: { limits: usageItems },
        }).unwrap();
      }

      await setVoices({
        id: companyId,
        companyVoicesSetRequestDto: { voiceIds: selectedVoiceIds },
      }).unwrap();

      await setLlmProviders({
        id: companyId,
        companyLlmProvidersSetRequestDto: { llmProviderIds: selectedLlmProviderIds },
      }).unwrap();

      await setTranscribers({
        id: companyId,
        companyTranscribersSetRequestDto: { transcriberIds: selectedTranscriberIds },
      }).unwrap();

      await refetchCompany();

      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
        data: {
          title: t('common.saved'),
          description: t('superAdminMainPage.companies.companyLimits.saveSuccess'),
        },
      });

      onSuccess?.();
      setActiveTab(0);
      closeDialog();
    } catch (error) {
      const extracted = extractErrorMessage(error);
      const voiceNameById = new Map<string, string>();
      for (const voice of catalogVoices ?? []) {
        voiceNameById.set(voice.id, voice.name);
      }
      for (const voice of company?.voices ?? []) {
        voiceNameById.set(voice.id, voice.name);
      }
      const providerTitleById = new Map<string, string>();
      for (const provider of catalogProviders ?? []) {
        providerTitleById.set(provider.id, provider.title);
      }
      for (const provider of company?.llmProviders ?? []) {
        providerTitleById.set(provider.id, provider.title);
      }
      showError({
        title: t('superAdminMainPage.companies.companyLimits.saveErrorTitle'),
        message: formatOptionLimitsConflictMessage(
          extracted.message,
          voiceNameById,
          providerTitleById,
        ),
        suggestion: t('superAdminMainPage.companies.companyLimits.saveErrorSuggestion'),
      });
    }
  };

  return (
    <Modal open={showDialogFlag} onClose={handleClose}>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 560 },
          maxHeight: '90vh',
          overflow: 'hidden',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 1,
          display: 'flex',
          flexDirection: 'column',
        }}>
        <Typography variant="h6" component="h2" align="center" sx={{ mb: 2 }}>
          {t('superAdminMainPage.companies.companyLimits.title')}
        </Typography>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          variant="fullWidth"
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label={t('superAdminMainPage.companies.company.usageLimits')} />
          <Tab label={t('superAdminMainPage.companies.company.optionLimits')} />
        </Tabs>

        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'visible', pr: 1, mr: -1 }}>
          {activeTab === 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('superAdminMainPage.companies.companyLimits.description')}
              </Typography>
              <Controller
                name="concurrentLimit"
                control={control}
                rules={{
                  validate: (v) =>
                    v !== '' && typeof v === 'number' && v >= 1
                      ? true
                      : t('superAdminMainPage.companies.companyLimits.parallelCallsRequired'),
                }}
                render={({ field }) => (
                  <TextField
                    margin="normal"
                    fullWidth
                    label={t('superAdminMainPage.companies.company.maxParallelCalls')}
                    type="number"
                    value={field.value}
                    onChange={(e) => {
                      const val = e.target.value;
                      field.onChange(val === '' ? '' : Math.max(1, Number(val)));
                    }}
                    inputProps={{ min: 1 }}
                    error={Boolean(errors.concurrentLimit)}
                    helperText={errors.concurrentLimit?.message}
                  />
                )}
              />
              <UsageLimitsFields control={control} />
            </Box>
          )}

          {activeTab === 1 && (
            <OptionLimitsFields
              companyId={companyId}
              enabled={showDialogFlag}
              selectedVoiceIds={selectedVoiceIds}
              onVoiceIdsChange={setSelectedVoiceIds}
              selectedLlmProviderIds={selectedLlmProviderIds}
              onLlmProviderIdsChange={setSelectedLlmProviderIds}
              selectedTranscriberIds={selectedTranscriberIds}
              onTranscriberIdsChange={setSelectedTranscriberIds}
              disabled={isSaving}
            />
          )}
        </Box>

        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          sx={{ mt: 3, pt: 2, flexShrink: 0 }}>
          <Button type="button" variant="outlined" onClick={handleClose} disabled={isSaving}>
            {t('superAdminMainPage.companies.operation.cancel')}
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={isSaving}>
            {t('superAdminMainPage.companies.operation.save')}
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};
